import { ContestChallengeBadgeIcon } from 'icons/badge/ContestChallengeIcon';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { BadgeType } from 'constants/cover';
import { useAuthState } from 'contexts/AuthProvider';
import { useContentState } from 'state-hooks/contents/useContentState';
import { CoverBadge } from 'ui-components/common/CoverBadge';
import { HiddenReservationBadge } from 'ui-components/contents/HiddenReservationBadge';

const variant = {
  container: 'flex flex-row justify-start items-center gap-[2px] h-6',
  hidden: 'rounded-[2px] border-[0.5px] border-white/10 bg-gray-600',
} as const;

interface ContentInlineBadgesProps {
  content: ContentDto | ContentPublicRelayDto;
  className?: string;
}

export const ContentInlineBadges = ({ content, className }: ContentInlineBadgesProps) => {
  const { partner } = useAuthState();
  const state = useContentState(content);

  const showPartner = useMemo(() => !!(partner && partner.verifiedAt), [partner]);
  const hasBadges = useMemo(
    () => state.shouldShowBadges || (showPartner && state.isProposalWanted),
    [state.shouldShowBadges, showPartner, state.isProposalWanted],
  );

  if (!hasBadges) {
    return null;
  }

  return (
    <div className={twMerge(variant.container, className)}>
      {state.isNew && <CoverBadge type={BadgeType.New} />}

      {state.isDirittoOriginal && <CoverBadge type={BadgeType.DirittoOriginal} />}

      {state.isDirittoOnly && <CoverBadge type={BadgeType.DirittoOnly} />}

      {state.isUpdated && <CoverBadge type={BadgeType.Up} />}

      {state.isCompleted && <CoverBadge type={BadgeType.Completed} />}

      {state.isPublished && <CoverBadge type={BadgeType.Published} />}

      {state.isContestActive && (
        <ContestChallengeBadgeIcon className="w-[69px] h-4 p-[3px] rounded-[2px] bg-gray-900 opacity-100" />
      )}

      {state.isContestWinner && <CoverBadge type={BadgeType.Contest} />}

      {state.isAdult && <CoverBadge type={BadgeType.AdultBox} />}

      {state.showHiddenReservationBadge && (
        <HiddenReservationBadge reservedHideContentsDiffDays={state.reservedHideContentsDiffDays} />
      )}

      {state.isHidden && (
        <div className={variant.hidden}>
          <CoverBadge type={BadgeType.Hidden} />
        </div>
      )}

      {showPartner && state.isProposalWanted && <CoverBadge type={BadgeType.ProposalWanted} />}
    </div>
  );
};
